import { useState, useEffect } from "react";
import {
  Eye, MousePointerClick, Search,
  ShoppingCart, DollarSign, Activity,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";

// ─── Buscar Visita por Código ─────────────────────────────────────────────────

function BuscarVisitaPorCodigo({ onResult }) {
  const [codigo, setCodigo] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const formatVisitUuid = (input) => {
    let code = String(input || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
    if (code.startsWith("svc_")) code = code.slice(4);
    if (code.startsWith("svc")) code = code.replace(/^svc_?/, "");
    if (code.length === 32) return `svc_${code}`;
    if (code.length === 36 && code.startsWith("svc_")) return code;
    return code.startsWith("svc_") ? code : `svc_${code}`;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setErro("");
    const code = formatVisitUuid(codigo);
    setLoading(true);
    try {
      const resp = await axios.get(
        `https://api.sobevenda.com.br/api-proxy/script/find-visit-by-uuid?visit_uuid=${code}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` } }
      );
      if (!resp.data?.visit_uuid) { setErro("Nenhuma visita encontrada com esse código."); onResult(null); }
      else { onResult(resp.data); }
    } catch { setErro("Erro ao buscar visita."); onResult(null); }
    setLoading(false);
  };

  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={handleSearch}>
      <input
        type="text"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        placeholder="Buscar visita por código svc_"
        className={`${inputCls} w-72`}
        autoFocus
      />
      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
      >
        <Search className="w-4 h-4" /> {loading ? "Buscando..." : "Buscar"}
      </button>
      {erro && <p className="text-sm text-red-500">{erro}</p>}
    </form>
  );
}

// ─── Métrica inline ───────────────────────────────────────────────────────────

function Metric({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
      <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
      <span className="font-medium text-sm">{value}</span>
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

function MonitoramentoAcessos() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscaVisita, setBuscaVisita] = useState(null);
  const navigate = useNavigate();

  const nomeSobeScript = buscaVisita && scripts.length
    ? scripts.find((s) => s.uuid === buscaVisita.script_uuid)?.project_name || ""
    : "";

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("https://api.sobevenda.com.br/api-proxy/script/list-sobescripts-active", {
          headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
        });
        setScripts(res.data || []);
      } catch { toast.error("Erro ao buscar SobeScripts."); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Monitoramento de Acessos
          </h1>
          <div className="mt-4">
            <BuscarVisitaPorCodigo onResult={setBuscaVisita} />
          </div>
        </div>

        {/* Resultado da busca */}
        {buscaVisita && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">Visita encontrada</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm mb-4">
              {[
                { label: "Código",     value: buscaVisita.visit_uuid },
                { label: "Data",       value: new Date(buscaVisita.created_at * 1000).toLocaleString("pt-BR") },
                { label: "IP",         value: buscaVisita.ip },
                { label: "Dispositivo", value: buscaVisita.device_type },
                { label: "Cidade",     value: buscaVisita.city },
                { label: "País",       value: buscaVisita.country },
                { label: "Origem",     value: buscaVisita.referrer || "Direto" },
                { label: "GCLID",      value: buscaVisita.gclid || "—" },
                { label: "SobeScript", value: nomeSobeScript || "—" },
              ].map(({ label, value }) => (
                <div key={label}>
                  <span className="text-slate-500 dark:text-slate-400">{label}: </span>
                  <span className="font-medium text-slate-900 dark:text-slate-100 break-all">{value}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate(`/sobescripts-analytics/${buscaVisita.script_uuid}?visit_uuid=${encodeURIComponent(buscaVisita.visit_uuid)}&ip=${encodeURIComponent(buscaVisita.ip)}`)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              <Eye className="w-4 h-4" /> Ver detalhes completos
            </button>
          </div>
        )}

        {/* Lista de scripts */}
        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Carregando...</p>
        ) : scripts.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nenhum SobeScript encontrado.</p>
        ) : (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]">
            {scripts.map((script) => {
              const isFunnel =
                script.is_funnel === 1 || script.is_funnel === "1" ||
                script.is_funnel === true || script.funnel_mode === "bridge_vsl";

              const visitCount    = Number(script.visit_count    || 0);
              const clickCount    = Number(script.click_count    || 0);
              const checkoutCount = Number(script.checkout_count || 0);
              const salesCount    = Number(script.sales_count    || 0);
              const bridgeVisits  = Number(script.bridge_visits  || 0);
              const vslVisits     = Number(script.vsl_visits     || 0);
              const bridgeClicks  = Number(script.bridge_clicks  || 0);
              const vslClicks     = Number(script.vsl_clicks     || 0);

              return (
                <div
                  key={script.uuid}
                  className="flex flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {/* Nome + tipo */}
                  <div className="mb-3">
                    <p className="text-sm font-bold text-slate-900 dark:text-slate-100 break-words leading-snug">
                      {script.project_name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500 dark:text-slate-400">{script.platform || "—"}</span>
                      {isFunnel ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          Funil Bridge + VSL
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                          Single Page
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Métricas */}
                  <div className="flex flex-wrap gap-x-5 gap-y-2 mb-3">
                    <Metric icon={Eye}              value={visitCount}    label="Visitas"    />
                    <Metric icon={MousePointerClick} value={clickCount}    label="Cliques"    />
                    <Metric icon={ShoppingCart}      value={checkoutCount} label="Checkouts"  />
                    <Metric icon={DollarSign}         value={salesCount}    label="Vendas"     />
                  </div>

                  {/* Detalhe por etapa (funil) */}
                  {isFunnel && (
                    <div className="mb-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 px-3 py-2 text-xs text-slate-600 dark:text-slate-400 space-y-1">
                      <div><span className="font-medium text-slate-700 dark:text-slate-300">Bridge:</span> {bridgeVisits} visitas · {bridgeClicks} cliques</div>
                      <div><span className="font-medium text-slate-700 dark:text-slate-300">VSL:</span> {vslVisits} visitas · {vslClicks} cliques</div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                    {script.auto_conversion === "yes" ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                        Conversão Automática
                      </span>
                    ) : <span />}

                    <Link
                      to={`/sobescripts-analytics/${script.uuid}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium"
                    >
                      <Eye className="w-3.5 h-3.5" /> Ver acessos
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MonitoramentoAcessos;